import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import Dialog from '../common/dialog'
import { FormError } from '../../types'
import { AuthContext } from '../../contexts'
import { numberWithCommas } from '../../utils'
import { useSocket } from '../../hooks/use-socket'
import SwapIcon from '../../assets/icons/swap-icon.svg'
import { requestAccessToken } from '../../api/auth.api'
import { countriesCurrencies, PocketMovementType } from '../../lib'
import { getPocketsTransfersByCountry, getPocketsTransfersByCustomerCompany } from '../../api/app-data.api'

export const PocketsTransfersModal = ({ selectedPocketTransferDetails, handleSetModalOpenState }: { selectedPocketTransferDetails?: any; handleSetModalOpenState: (openState: boolean) => void }) => {
  const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
  const currency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toUpperCase()

  const navigate = useNavigate()
  const location = useLocation()
  const { customerCompanyId } = useParams()
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { t } = useTranslation(['pockets-management', 'common'])
  const { appDataSocket, isAppDataSocketInitialized } = useSocket()

  const [errors, setErrors] = useState<FormError>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [pocketsTransfers, setPocketsTransfers] = useState<Array<any>>([])

  useEffect(() => {
    if (!appDataSocket) return

    appDataSocket.on('addNewPocketMovement', async (dataFromSocket: any) => {
      const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
      const selectedCurrency = (countriesCurrencies as { [key: string]: string })[selectedCountry].toLowerCase()

      if (selectedCurrency === dataFromSocket.currency) {
        if (location.pathname.indexOf('customer-company-money-movements') > -1) {
          handleGetPocketTransfersByCustomerCompanyFromRemote()
        } else {
          handleGetPocketTransfersFromRemote()
        }
      }
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appDataSocket, isAppDataSocketInitialized])

  useEffect(() => {
    handleGetPocketsTransfers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleGetPocketsTransfers = async () => {
    if (location.pathname.indexOf('customer-company-money-movements') > -1) {
      let localPocketsTransfersByCustomerCompanyToSet: any = {}
      const pocketId = Array.isArray(selectedPocketTransferDetails) ? selectedPocketTransferDetails[0]._id : selectedPocketTransferDetails._id
      const localPocketsTransfersByCustomerCompany = globalThis.sessionStorage.getItem(`pocketTransferDetails:${String(pocketId)}`)

      if (localPocketsTransfersByCustomerCompany) {
        localPocketsTransfersByCustomerCompanyToSet = JSON.parse(localPocketsTransfersByCustomerCompany)
        handleSetPocketTransfersByCustomerCompanyInitialData(localPocketsTransfersByCustomerCompanyToSet)
      }

      handleGetPocketTransfersByCustomerCompanyFromRemote()
      return
    }

    let localPocketTransfersToSet: any = {}
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'
    const localPocketTransfers = globalThis.sessionStorage.getItem(`pocketTransfers:${selectedCountry}`)

    if (localPocketTransfers) {
      localPocketTransfersToSet = JSON.parse(localPocketTransfers)
      handleSetPocketTransfersInitialData(localPocketTransfersToSet)
    }

    handleGetPocketTransfersFromRemote()
  }

  const handleGetPocketTransfersByCustomerCompanyFromRemote = async () => {
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const pocketId = Array.isArray(selectedPocketTransferDetails) ? selectedPocketTransferDetails[0]._id : selectedPocketTransferDetails._id
      const { data } = await getPocketsTransfersByCustomerCompany(String(customerCompanyId), selectedCountry, String(pocketId), accessToken)

      globalThis.sessionStorage.setItem(`pocketTransferDetails:${String(pocketId)}`, JSON.stringify(data))
      handleSetPocketTransfersByCustomerCompanyInitialData(data)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }
  }

  const handleGetPocketTransfersFromRemote = async () => {
    const selectedCountry = globalThis.sessionStorage.getItem('selectedCountry') || 'COL'

    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(isAuth?.replaceAll('"', '') ?? '')
      const { data } = await getPocketsTransfersByCountry(selectedCountry, accessToken)

      globalThis.sessionStorage.setItem(`pocketTransfers:${selectedCountry}`, JSON.stringify(data))
      handleSetPocketTransfersInitialData(data)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      } else if (error?.response?.status === 401) {
        setErrors({ generalError: t('formGeneralErrors.unauthorized', { ns: 'common' }) })
      } else {
        setErrors({ generalError: t('formGeneralErrors.serverError', { ns: 'common' }) })
      }
    }
  }

  const handleSetPocketTransfersInitialData = (dataToSet: any) => {
    setPocketsTransfers(dataToSet.pocketsTransfersData.sort((a: any, b: any) => (b.consecutiveNumber ?? 0) - (a.consecutiveNumber ?? 0)))
    setIsLoading(false)
  }

  const handleSetPocketTransfersByCustomerCompanyInitialData = (dataToSet: any) => {
    setPocketsTransfers(dataToSet.pocketsTransfersData)
    setIsLoading(false)
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <Dialog
      errors={errors}
      minWidth="700px"
      maxHeight="800px"
      hideActionsButtons
      dialogIsLoading={isLoading}
      dialogTitle={t('walletsTransfersModal.title')}
      handleDialogOpenState={(openState: boolean) => handleSetModalOpenState(openState)}
    >
      {pocketsTransfers.length > 0 ? (
        <>
          <div className="pocket-transfer-headers-container">
            <div className="pocket-transfer-header">
              <div className="pocket-transfer-header-container">{t('walletsTransfersModal.from')}</div>
            </div>
            <div className="pocket-transfer-header">
              <div
                className="pocket-transfer-header-container text-align-right"
                style={{ paddingRight: '16px', boxSizing: 'border-box' }}
              >
                {t('walletsTransfersModal.to')}
              </div>
            </div>
          </div>
          <div className={pocketsTransfers.length >= 6 ? 'display-flex-column gap-10 margin-top-16 width-100 padding-right-10' : 'display-flex-column gap-10 margin-top-16 width-100'}>
            {pocketsTransfers.map((pocketTransfer: any) => {
              return (
                <div
                  key={pocketTransfer._id}
                  className="pocket-transfer-info-container"
                >
                  <div className="pocket-transfer-info-container-top">
                    {pocketTransfer.movementType === PocketMovementType.CREDITING_FUNDS ? (
                      <div className="display-flex-column gap-10 width-33">
                        <div className="pockets-movements-font-style font-size-15">{t('walletsTransfersModal.creditingFunds')}</div>
                      </div>
                    ) : (
                      <div className="display-flex-column gap-10 width-33">
                        <div className="pockets-movements-font-style font-size-15">{pocketTransfer.fromPocketName.toUpperCase()}</div>
                        <div className="display-flex-column gap-5">
                          <div className="pockets-movements-font-style font-size-12">{`${t('walletsTransfersModal.before')} $ ${numberWithCommas(
                            Number(pocketTransfer.transferData.fromPocketBeforeBalance)
                          )} ${currency}`}</div>
                          <div className="pockets-movements-font-style font-size-12">{`${t('walletsTransfersModal.after')} $ ${numberWithCommas(
                            Number(pocketTransfer.transferData.fromPocketAfterBalance)
                          )} ${currency}`}</div>
                        </div>
                      </div>
                    )}
                    <div className="display-flex-column gap-8 align-items-center width-33">
                      <div className="pockets-movements-font-style font-size-17">{`$ ${numberWithCommas(Number(pocketTransfer.movementAmount))} ${currency}`}</div>
                      <img
                        src={SwapIcon}
                        alt="swap-icon"
                        style={{
                          width: '24px',
                          height: '24px'
                        }}
                      />
                    </div>
                    <div className="display-flex-column gap-10 width-33">
                      <div className="pockets-movements-font-style font-size-15">{pocketTransfer.toPocketName.toUpperCase()}</div>
                      {pocketTransfer.movementType === PocketMovementType.CREDITING_FUNDS ? (
                        <div className="display-flex-column gap-5">
                          <div className="pockets-movements-font-style font-size-12">{`${t('walletsTransfersModal.before')} $ ${numberWithCommas(Number(pocketTransfer.balanceBefore))} ${currency}`}</div>
                          <div className="pockets-movements-font-style font-size-12">{`${t('walletsTransfersModal.after')} $ ${numberWithCommas(Number(pocketTransfer.balanceAfter))} ${currency}`}</div>
                        </div>
                      ) : (
                        <div className="display-flex-column gap-5">
                          <div className="pockets-movements-font-style font-size-12">{`${t('walletsTransfersModal.before')} $ ${numberWithCommas(
                            Number(pocketTransfer.transferData.toPocketBeforeBalance)
                          )} ${currency}`}</div>
                          <div className="pockets-movements-font-style font-size-12">{`${t('walletsTransfersModal.after')} $ ${numberWithCommas(
                            Number(pocketTransfer.transferData.toPocketAfterBalance)
                          )} ${currency}`}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="pocket-transfer-info-container-bottom pockets-movements-font-style">
                    {t('walletsTransfersModal.createdAt')} {format(new Date(String(pocketTransfer?.createdAt)), 'dd/MM/yyyy | HH:mm:ss')}
                  </div>
                </div>
              )
            })}
          </div>
        </>
      ) : (
        <div className="empty-pockets-transfers">{t('walletsTransfersModal.emptyMovements')}</div>
      )}
    </Dialog>
  )
}
